import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getFacilities, deleteFacility } from "wasp/client/operations";
import { toast } from "../../../hooks/use-toast";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  MapPin,
  Building2,
  Wifi,
  Car,
  User,
  ExternalLink,
  Edit3,
  Trash2,
  Eye,
  Pin,
  Archive,
  Clock,
  Users,
  Calendar,
  Grid3X3,
  List,
  Table,
  Zap,
  Coffee,
  Monitor,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { cn } from "../../../lib/utils";
import { Button } from "../../../components/ui/button";
import { DashboardLayout } from "../../../client/components/Sidebar";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../../client/hooks/useRequireSectionVisible";
import {
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../../components/ui/enhanced-page-layout";

type FacilityCategory = "vending" | "equipment" | "rooms" | "coworking" | "all";
type FacilityType = "available" | "reservable" | "walkin" | "all";

const FACILITY_CATEGORIES = [
  { value: "all", label: "All Facilities", icon: Filter },
  { value: "vending", label: "Vending Machines", icon: Coffee },
  { value: "equipment", label: "Equipment", icon: Monitor },
  { value: "rooms", label: "Rooms", icon: Building2 },
  { value: "coworking", label: "Co-Working Spaces", icon: Users },
];

const FACILITY_TYPES = [
  { value: "all", label: "All Types" },
  { value: "available", label: "Available" },
  { value: "reservable", label: "Reservable" },
  { value: "walkin", label: "Walk-in Only" },
];

export default function FacilitiesPage() {
  useRequireSectionVisible("showFacility");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();

  // State for filters and search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<FacilityCategory>("all");
  const [selectedType, setSelectedType] = useState<FacilityType>("all");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedDate, setSelectedDate] = useState("All");
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  const deleteFacilityAction = useAction(deleteFacility);

  // Fetch all facilities (no server-side filtering to avoid re-renders)
  const {
    data: allFacilities = [],
    isLoading,
    error,
  } = useQuery(
    getFacilities,
    {
      locationId: selectedLocation?.id || "",
      filters: {
        // Remove server-side filtering to prevent query refetching during typing
      },
    },
    {
      enabled: !!selectedLocation?.id, // Only run query if we have a location ID
    },
  );

  // Extract unique values for filters (use all facilities, not filtered)
  const uniqueCategories = useMemo(() => {
    const cats = new Set(
      allFacilities.map((f: any) => f.category).filter(Boolean),
    );
    return Array.from(cats).sort() as string[];
  }, [allFacilities]);

  const uniqueStatuses = useMemo(() => {
    const statuses = new Set<string>();
    allFacilities.forEach((f: any) => {
      const isAvailable = f.metadata?.isAvailable !== false;
      statuses.add(isAvailable ? "Available" : "Unavailable");
    });
    return Array.from(statuses).sort();
  }, [allFacilities]);

  const uniqueDates = useMemo(() => {
    const dateMap = new Map<string, string>();
    allFacilities.forEach((f: any) => {
      const date = new Date(f.createdAt);
      const dateStr = date.toLocaleDateString();
      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, f.createdAt);
      }
    });
    const unique = Array.from(dateMap.keys());
    return unique.sort((a, b) => {
      const dateA = new Date(dateMap.get(a)!);
      const dateB = new Date(dateMap.get(b)!);
      return dateB.getTime() - dateA.getTime();
    });
  }, [allFacilities]);

  // Filter facilities based on client-side filters
  const filteredFacilities = useMemo(() => {
    return (allFacilities as any[]).filter((facility: any) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (
          !facility.title.toLowerCase().includes(searchLower) &&
          !facility.description?.toLowerCase().includes(searchLower) &&
          !facility.contentBody?.toLowerCase().includes(searchLower) &&
          !facility.category?.toLowerCase().includes(searchLower) &&
          !facility.tags.some((tag: string) =>
            tag.toLowerCase().includes(searchLower),
          )
        ) {
          return false;
        }
      }

      // Category filter
      if (
        selectedCategory !== "all" &&
        facility.category !== selectedCategory
      ) {
        return false;
      }

      // Type filter
      if (selectedType !== "all") {
        // Map facility type to metadata
        const facilityType = facility.metadata?.type || "available";
        if (selectedType === "available" && facilityType !== "available")
          return false;
        if (selectedType === "reservable" && facilityType !== "reservable")
          return false;
        if (selectedType === "walkin" && facilityType !== "walkin")
          return false;
      }

      // Status filter
      if (selectedStatus !== "All") {
        const isAvailable = facility.metadata?.isAvailable !== false;
        const status = isAvailable ? "Available" : "Unavailable";
        if (status !== selectedStatus) return false;
      }

      // Date filter
      if (selectedDate !== "All") {
        const facilityDate = new Date(facility.createdAt).toLocaleDateString();
        if (facilityDate !== selectedDate) return false;
      }

      return true;
    });
  }, [
    allFacilities,
    searchTerm,
    selectedCategory,
    selectedType,
    selectedStatus,
    selectedDate,
  ]);

  // Sort facilities
  const sortedFacilities = useMemo(() => {
    return [...filteredFacilities].sort((a, b) => {
      // Sort pinned items to top
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;

      // Apply column sorting
      if (sortField === "title") {
        return sortDirection === "asc"
          ? a.title.localeCompare(b.title)
          : b.title.localeCompare(a.title);
      }
      if (sortField === "category") {
        return sortDirection === "asc"
          ? (a.category || "").localeCompare(b.category || "")
          : (b.category || "").localeCompare(a.category || "");
      }
      if (sortField === "status") {
        const statusA =
          a.metadata?.isAvailable !== false ? "Available" : "Unavailable";
        const statusB =
          b.metadata?.isAvailable !== false ? "Available" : "Unavailable";
        return sortDirection === "asc"
          ? statusA.localeCompare(statusB)
          : statusB.localeCompare(statusA);
      }
      if (sortField === "date") {
        return sortDirection === "asc"
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }

      // Default: newest first
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [filteredFacilities, sortField, sortDirection]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "vending":
        return <Coffee className="w-4 h-4" />;
      case "equipment":
        return <Monitor className="w-4 h-4" />;
      case "rooms":
        return <Building2 className="w-4 h-4" />;
      case "coworking":
        return <Users className="w-4 h-4" />;
      default:
        return <MapPin className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "vending":
        return "text-secondary";
      case "equipment":
        return "text-success";
      case "rooms":
        return "text-accent";
      case "coworking":
        return "text-primary";
      default:
        return "text-muted-foreground";
    }
  };

  const getAvailabilityText = (facility: any) => {
    const isAvailable = facility.metadata?.isAvailable !== false;
    return (
      <span
        className={`text-sm font-medium ${isAvailable ? "text-success" : "text-destructive"
          }`}
      >
        {isAvailable ? "Available" : "Unavailable"}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600">Please log in to view facilities.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              No Location Selected
            </h2>
            <p className="text-gray-600">
              Please select a location to view facilities.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className="w-full h-full flex flex-col">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-20 bg-white border-b border-gray-200 transition-all duration-300">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Facilities & Site
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Explore available facilities, equipment, and make reservations
                  at {selectedLocation?.name || "All Locations"} (
                  {sortedFacilities.length} facilities)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  icon={Filter}
                  label=""
                  className={cn("hidden lg:flex", isFilterExpanded ? "opacity-80" : "")}
                />
                
                {/* Mobile Filter Button */}
                <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
                  <EnhancedActionButton
                    onClick={() => setIsFilterExpanded(true)}
                    icon={Filter}
                    label=""
                    variant={isFilterExpanded ? "primary" : "secondary"}
                    className="lg:hidden"
                  />
                  <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                    <SheetHeader>
                      <SheetTitle>Filters</SheetTitle>
                    </SheetHeader>
                    <div className="py-6 space-y-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Search</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <input
                            type="text"
                            placeholder="All"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Category</label>
                        <select
                          value={selectedCategory}
                          onChange={(e) =>
                            setSelectedCategory(e.target.value as FacilityCategory)
                          }
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">All</option>
                          {uniqueCategories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Status</label>
                        <select
                          value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="All">All</option>
                          {uniqueStatuses.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Date</label>
                        <select
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="All">All</option>
                          {uniqueDates.map((date) => (
                            <option key={date} value={date}>
                              {date}
                            </option>
                          ))}
                        </select>
                      </div>

                      <Button 
                        className="w-full" 
                        onClick={() => setIsFilterExpanded(false)}
                      >
                        Apply Filters
                      </Button>
                    </div>
                  </SheetContent>
                </Sheet>
                {user?.isAdmin && (
                  <EnhancedActionButton
                    onClick={() => {
                      if (selectedLocation?.locationCode) {
                        navigate(
                          `/locations/${selectedLocation.locationCode}/facilities/add`,
                        );
                      } else {
                        navigate("/facilities/add");
                      }
                    }}
                    icon={Plus}
                    label="Add Facility"
                    hideLabelOnMobile
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Table Header - Only for table view */}
        {!isLoading &&
          !error &&
          (
            <div 
              className={cn(
                "fixed top-[calc(var(--header-height)+5rem)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-sidebar-hover border-b border-border transition-all duration-300 overflow-hidden hidden lg:block",
                isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
              )}
            >
              <div className="px-6 py-3 space-y-3">
                {/* Column Headers */}
                <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
                   <div className="col-span-4">FACILITY</div>
                   <div className="col-span-2">CATEGORY</div>
                   <div className="col-span-2">STATUS</div>
                   <div className={user?.isAdmin ? "col-span-2" : "col-span-4"}>ADDED</div>
                   {user?.isAdmin && <div className="col-span-2 text-right">ACTIONS</div>}
                 </div>
                {/* Column Filters */}
                <div className="grid grid-cols-12 gap-4">
                  {/* Facility Filter */}
                  <div className="col-span-4">
                    <input
                      type="text"
                      placeholder="All"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  {/* Category Filter */}
                  <div className="col-span-2">
                    <select
                      value={selectedCategory}
                      onChange={(e) =>
                        setSelectedCategory(e.target.value as FacilityCategory)
                      }
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All</option>
                      {uniqueCategories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Status Filter */}
                  <div className="col-span-2">
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="All">All</option>
                      {uniqueStatuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Date Filter */}
                  <div className="col-span-2">
                    <select
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="All">All</option>
                      {uniqueDates.map((date) => (
                        <option key={date} value={date}>
                          {date}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Actions - No filter */}
                  <div className="col-span-2">
                    <div className="w-full px-3 py-2"></div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Scrollable Content */}
        <div
          className={`flex-1 overflow-y-auto transition-[padding] duration-300 ${!isLoading &&
            !error
            ? isFilterExpanded && !isMobile
              ? (viewMode === "table" ? "pt-[calc(var(--header-height)+11rem)]" : "pt-[calc(var(--header-height)+8rem)]")
              : "pt-[calc(var(--header-height)+5rem)]"
            : "pt-[calc(var(--header-height)+5rem)]"
            }`}
        >
          <div className="w-full">
            {/* Loading State */}
            {isLoading && (
              <div className={enhancedPageLayoutStyles.states.loading}>
                <div
                  className={enhancedPageLayoutStyles.states.loadingSpinner}
                ></div>
                <p className={enhancedPageLayoutStyles.states.loadingText}>
                  Loading facilities...
                </p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className={enhancedPageLayoutStyles.states.error}>
                <MapPin className={enhancedPageLayoutStyles.states.errorIcon} />
                <h3 className={enhancedPageLayoutStyles.states.errorTitle}>
                  Error Loading Facilities
                </h3>
                <p className={enhancedPageLayoutStyles.states.errorText}>
                  Please try again later.
                </p>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && sortedFacilities.length === 0 && (
              <div className={enhancedPageLayoutStyles.states.empty}>
                <MapPin className={enhancedPageLayoutStyles.states.emptyIcon} />
                <h3 className={enhancedPageLayoutStyles.states.emptyTitle}>
                  No Facilities Found
                </h3>
                <p className={enhancedPageLayoutStyles.states.emptyText}>
                  {searchTerm ||
                    selectedCategory !== "all" ||
                    selectedType !== "all" ||
                    selectedStatus !== "All" ||
                    selectedDate !== "All"
                    ? "Try adjusting your search or filters to find what you're looking for."
                    : "Be the first to add a facility in this location!"}
                </p>
              </div>
            )}

            {/* Content */}
            {!isLoading && !error && sortedFacilities.length > 0 && (
              <>
                {/* Cards View */}
                {viewMode === "cards" && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {sortedFacilities.map((facility: any) => (
                        <div
                          key={facility.id}
                          className={`${enhancedPageLayoutStyles.card.container
                            } ${facility.isPinned ? "border-blue-500 shadow-sm" : ""
                            } cursor-pointer hover:shadow-md transition-shadow`}
                          onClick={() => navigate(`/facilities/${facility.id}`)}
                        >
                          <div
                            className={enhancedPageLayoutStyles.card.content}
                          >
                            <div
                              className={enhancedPageLayoutStyles.card.header}
                            >
                              <div className="flex items-center gap-2">
                                {getCategoryIcon(facility.category)}
                                <span
                                  className={`text-sm font-medium ${getCategoryColor(
                                    facility.category,
                                  )}`}
                                >
                                  {facility.category}
                                </span>
                              </div>
                              {facility.isPinned && (
                                <Pin className="w-4 h-4 text-primary" />
                              )}
                            </div>

                            <h3 className={enhancedPageLayoutStyles.card.title}>
                              {facility.title}
                            </h3>
                            {facility.description && (
                              <p
                                className={
                                  enhancedPageLayoutStyles.card.description
                                }
                              >
                                {facility.description}
                              </p>
                            )}

                            <div className="flex items-center justify-between mb-4">
                              {getAvailabilityText(facility)}
                              <span className="text-xs text-content-text-muted">
                                {formatDate(facility.createdAt)}
                              </span>
                            </div>

                            <div
                              className={enhancedPageLayoutStyles.card.footer}
                            >
                              <div
                                className={enhancedPageLayoutStyles.card.meta}
                              >
                                <div className="flex items-center gap-2 text-sm text-content-text-muted">
                                  <User className="w-4 h-4" />
                                  <span>
                                    {facility.author?.username ||
                                      facility.author?.email}
                                  </span>
                                </div>
                              </div>
                              <div
                                className={
                                  enhancedPageLayoutStyles.card.actions
                                }
                                onClick={(e) => e?.stopPropagation()}
                              >
                                <EnhancedIconButton
                                  onClick={() =>
                                    navigate(`/facilities/${facility.id}`)
                                  }
                                  icon={Eye}
                                  title="View details"
                                />
                                {user?.isAdmin && (
                                  <>
                                    <EnhancedIconButton
                                      onClick={() =>
                                        navigate(
                                          `/facilities/${facility.id}/edit`,
                                        )
                                      }
                                      icon={Edit3}
                                      title="Edit facility"
                                    />
                                    <EnhancedIconButton
                                      onClick={async (e) => {
                                        if (e) e.stopPropagation();
                                        if (
                                          confirm(
                                            "Are you sure you want to delete this facility?",
                                          )
                                        ) {
                                          try {
                                            await deleteFacilityAction({ facilityId: facility.id });
                                            toast({ title: "Success", description: "Facility deleted successfully" });
                                          } catch (err: any) {
                                            toast({ title: "Error", description: err.message || "Failed to delete facility", variant: "destructive" });
                                          }
                                        }
                                      }}
                                      icon={Trash2}
                                      title="Delete facility"
                                      variant="danger"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* List View */}
                {viewMode === "list" && (
                  <div className="p-4">
                    <div className="space-y-3 sm:space-y-4">
                      {sortedFacilities.map((facility: any) => (
                        <div
                          key={facility.id}
                          className={`bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow ${facility.isPinned ? "border-blue-500 shadow-sm" : ""
                            } cursor-pointer hover:bg-gray-50 transition-colors`}
                          onClick={() => navigate(`/facilities/${facility.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                {getCategoryIcon(facility.category)}
                                <span
                                  className={`text-sm font-medium ${getCategoryColor(
                                    facility.category,
                                  )}`}
                                >
                                  {facility.category}
                                </span>
                                {facility.isPinned && (
                                  <Pin className="w-4 h-4 text-blue-600" />
                                )}
                              </div>

                              <div>
                                <h3 className="font-semibold text-gray-900 text-xl mb-2">
                                  {facility.title}
                                </h3>
                                {facility.description && (
                                  <p className="text-gray-600 mb-3 line-clamp-2">
                                    {facility.description}
                                  </p>
                                )}

                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  {getAvailabilityText(facility)}
                                  <div className="flex items-center gap-1">
                                    <User className="w-4 h-4" />
                                    <span>
                                      {facility.author?.username ||
                                        facility.author?.email}
                                    </span>
                                  </div>
                                  <span>{formatDate(facility.createdAt)}</span>
                                </div>
                              </div>
                            </div>

                            <div
                              className="flex items-center gap-4"
                              onClick={(e) => e?.stopPropagation()}
                            >
                              <EnhancedIconButton
                                onClick={() =>
                                  navigate(`/facilities/${facility.id}`)
                                }
                                icon={Eye}
                                title="View details"
                              />
                              {facility.author?.id === user.id && (
                                <>
                                  <EnhancedIconButton
                                    onClick={() =>
                                      navigate(
                                        `/facilities/${facility.id}/edit`,
                                      )
                                    }
                                    icon={Edit3}
                                    title="Edit facility"
                                  />
                                  <EnhancedIconButton
                                    onClick={async (e) => {
                                      if (e) e.stopPropagation();
                                      if (
                                        confirm(
                                          "Are you sure you want to delete this facility?",
                                        )
                                      ) {
                                        try {
                                          await deleteFacilityAction({ facilityId: facility.id });
                                          toast({ title: "Success", description: "Facility deleted successfully" });
                                        } catch (err: any) {
                                          toast({ title: "Error", description: err.message || "Failed to delete facility", variant: "destructive" });
                                        }
                                      }
                                    }}
                                    icon={Trash2}
                                    title="Delete facility"
                                    variant="danger"
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Table View */}
                {viewMode === "table" && (
                  <div className="w-full">
                    {/* Table Body - No internal scroll */}
                    <div className="divide-y divide-gray-200">
                      {sortedFacilities.map((facility: any) => (
                        <div
                          key={facility.id}
                          className="px-6 py-3 hover:bg-hover transition-colors cursor-pointer"
                          onClick={() => navigate(`/facilities/${facility.id}`)}
                        >
                          <div className="grid grid-cols-12 gap-4 items-start">
                            <div className="col-span-4">
                              <div className="flex items-start">
                                {facility.isPinned && (
                                  <Pin className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {facility.title}
                                  </div>
                                  {facility.description && (
                                    <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                                      {facility.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-span-2">
                              <div className="flex items-center">
                                {getCategoryIcon(facility.category)}
                                <span className="ml-2 text-sm text-gray-600 capitalize">
                                  {facility.category}
                                </span>
                              </div>
                            </div>
                            <div className="col-span-2">
                              {getAvailabilityText(facility)}
                            </div>
                            <div className={user?.isAdmin ? "col-span-2" : "col-span-4"}>
                              <span className="text-sm text-gray-500">
                                {formatDate(facility.createdAt)}
                              </span>
                            </div>
                            {user?.isAdmin && (
                              <div className="col-span-2 flex items-center justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                                <EnhancedIconButton
                                  onClick={() =>
                                    navigate(
                                      `/facilities/${facility.id}/edit`,
                                    )
                                  }
                                  icon={Edit3}
                                  title="Edit facility"
                                />
                                <EnhancedIconButton
                                  onClick={async (e) => {
                                    if (e) e.stopPropagation();
                                    if (
                                      confirm(
                                        "Are you sure you want to delete this facility?",
                                      )
                                    ) {
                                      try {
                                        await deleteFacilityAction({ facilityId: facility.id });
                                        toast({ title: "Success", description: "Facility deleted successfully" });
                                      } catch (err: any) {
                                        toast({ title: "Error", description: err.message || "Failed to delete facility", variant: "destructive" });
                                      }
                                    }
                                  }}
                                  icon={Trash2}
                                  title="Delete facility"
                                  variant="danger"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
